import React, { FC } from 'react';

import {
  Text,
  Flex,
  Tooltip,
  Box,
  Center,
  IconButton,
  Link,
} from '@chakra-ui/react';
import { Report } from 'types/report';
import { routesSettings } from 'constants/routes';
import { convertUTCToLocal } from 'utils/dateUtils';
import CopyAsCSv from 'components/primitives/CopyAsCSv';
import { calculateLost } from 'utils/calculateLost';
import { formatNumberToDecimal } from 'utils/formatNumberToDecimal';
import { formatCurrency, formatNumber } from 'utils/formatNumbers';
import { FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';
import { calculateValues, returnRate, returnRateEach } from './utils';

type SkuDetailProps = {
  report: Report;
};

// Renders a "X Deliveries" / "Y Pickups" count. When a site id is available it
// becomes a hyperlink that opens the orders/pickups table (filtered to that
// customer) in a NEW tab; otherwise it falls back to plain text.
const renderCountLink = (
  count: number | undefined,
  label: string,
  basePath: string,
  siteId?: string,
) => {
  const text = `${count ?? 0} ${label}`;

  if (!siteId) {
    return <Text fontWeight="semibold">{text}</Text>;
  }

  return (
    <Link
      href={`${basePath}?venue=${siteId}`}
      isExternal
      fontWeight="semibold"
      color="primary.500"
      textDecoration="underline">
      {text}
    </Link>
  );
};

const SkuDetailTab: FC<SkuDetailProps> = ({ report }) => {
  const details = report.details || [];
  const siteId = report?.site?._id;
  let csvData = [];

  const values = calculateValues(details);

  const formatCsvData = () => {
    const headers = [
      'SKU',
      'Beginning Inventory',
      'Delivered',
      'Returned Dirty',
      'Returned Unused',
      'Ending Inventory',
      'Lost',
      'Used',
      'Return Rate',
    ];

    const dataRows = details.map((item) => {
      const lost = calculateLost(item);
      const used = item.ret + lost;
      const returnRate = returnRateEach(item) + '%';

      return [
        item.sku?.name || '',
        item.beg || 0,
        item.del || 0,
        item.ret || 0,
        item.unu || 0,
        item.end || 0,
        lost,
        used,
        returnRate,
      ];
    });
    const lastRow = [
      'Total Items',
      values.beginning || 0,
      values.totalDelivered || 0,
      values.returnedDirty || 0,
      values.returnedUnused || 0,
      values.ending || 0,
      values.lost || 0,
      values.used || 0,
      returnRate(values) + '%',
    ];

    const csvJson = [headers, dataRows, lastRow];

    return csvJson;
  };

  csvData = formatCsvData();
  return (
    <Flex flex={1} direction="column" mb="20px">
      <Flex
        flex={1}
        direction="column"
        maxH="100%"
        overflow="auto"
        fontSize="14px"
        fontWeight="400">
        <Flex dir="row">
          <Flex
            flex={1}
            direction="row"
            bg="surface.400"
            align="center"
            textAlign="center">
            <Box position="relative" width="23%">
              <Text h="98px" fontWeight="semibold" alignContent="center">
                Items
              </Text>

              {details.map((item, index) => {
                return (
                  <Flex
                    key={`${item.sku._id}-${index}`}
                    flex={1}
                    minH="51px"
                    maxH="51px"
                    textOverflow="ellipsis"
                    p="15px"
                    bg={index % 2 === 0 ? 'surface.200' : 'surface.100'}
                    borderBottom={
                      index % 2 === 0 ? 'surface.200' : 'surface.100'
                    }>
                    <Tooltip
                      label={item.sku.name}
                      hasArrow
                      placement="top-start">
                      <Text
                        flex={1.5}
                        noOfLines={item.sku.name.length > 16 ? 2 : 1}
                        fontSize={item.sku.name.length > 16 ? '12px' : '14px'}
                        textOverflow="ellipsis">
                        {item.sku.name}
                      </Text>
                    </Tooltip>
                  </Flex>
                );
              })}
              <Flex>
                <Text
                  bg="#B5E3BA"
                  flex={1.5}
                  minW="120px"
                  p="15px"
                  fontWeight="bold">
                  Total Items
                </Text>
              </Flex>
            </Box>

            {/* BOX 1 */}
            <Box position="relative" w="12rem">
              <Center pt={2}>
                <Text fontSize="xs" fontWeight="bold">
                  Available this period
                </Text>
              </Center>
              <Flex
                flex={1}
                direction="row"
                bg="surface.400"
                p="15px"
                fontWeight="semibold">
                <Text flex={0.5}>Beginning Inventory</Text>
                <Text flex={0.5}>Delivered</Text>
              </Flex>
              {details.map((item, index) => {
                let lost = calculateLost(item);
                let used = item.ret + lost;

                return (
                  <Flex
                    key={`${item.sku._id}-${index}`}
                    flex={1}
                    direction="row"
                    bg={index % 2 === 0 ? 'surface.200' : 'surface.100'}>
                    <Text
                      flex={0.5}
                      p="15px"
                      borderBottom={
                        index % 2 === 0 ? 'surface.200' : 'surface.100'
                      }>
                      {formatNumber(item.beg)}
                    </Text>
                    <Text flex={0.5} p="15px">
                      {formatNumber(item.del)}
                    </Text>
                  </Flex>
                );
              })}
              <Flex w="12rem" bg="#B5E3BA" zIndex="100">
                <Text flex={0.5} p="15px" fontWeight="bold">
                  {formatNumber(values.beginning)}
                </Text>
                <Text flex={0.5} p="15px" fontWeight="bold">
                  {formatNumber(values.totalDelivered)}
                </Text>
              </Flex>
              <Box
                borderRadius="10px"
                border="1px solid #2FB64A"
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex={100}
                flexGrow={1}></Box>
            </Box>

            {/*   BOX 2    */}
            <Box position="relative" w="16rem">
              <Center pt={2}>
                <Text fontSize="xs" fontWeight="bold">
                  Accounted For
                </Text>
              </Center>
              <Flex
                flex={1}
                direction="row"
                bg="surface.400"
                p="15px"
                fontWeight="semibold">
                <Text flex={0.5}>Returned Dirty</Text>
                <Text flex={0.5}>Returned Unused</Text>
                <Text flex={0.5}>Ending Inventory</Text>
              </Flex>
              {details.map((item, index) => {
                let lost = calculateLost(item);
                let used = item.ret + lost;

                return (
                  <Flex
                    key={`${item.sku._id}-${index}`}
                    flex={1}
                    direction="row"
                    bg={index % 2 === 0 ? 'surface.200' : 'surface.100'}>
                    <Text
                      flex={0.5}
                      p="15px"
                      borderBottom={
                        index % 2 === 0 ? 'surface.200' : 'surface.100'
                      }>
                      {formatNumber(item.ret)}
                    </Text>
                    <Text flex={0.5} p="15px">
                      {formatNumber(item.unu)}
                    </Text>
                    <Text flex={0.5} p="15px">
                      {formatNumber(item.end)}
                    </Text>
                  </Flex>
                );
              })}
              <Flex
                bg="#B5E3BA"
                flex={1}
                zIndex="100"
                direction="row"
                fontWeight="bold"
                maxW="16rem">
                <Text flex={0.5} p="15px">
                  {formatNumber(values.returnedDirty)}
                </Text>
                <Text flex={0.5} p="15px">
                  {formatNumber(values.returnedUnused)}
                </Text>
                <Text flex={0.5} p="15px">
                  {formatNumber(values.ending)}
                </Text>
              </Flex>
              <Box
                borderRadius="10px"
                borderY="1px solid #2FB64A"
                borderRight="1px solid #2FB64A"
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex={100}
                flexGrow={1}></Box>
            </Box>

            {/*   BOX RED    */}
            <Box
              position="relative"
              w="5.8rem"
              borderRadius="10px"
              borderY="1px solid #F4A0AF"
              borderRight="1px solid #F4A0AF">
              <Center
                flex={1}
                flexDir="column"
                borderTopRadius="10px"
                bg="#F1C1C8"
                h="98px"
                justifyContent="center"
                zIndex={200}
                fontWeight="semibold">
                <Text>Lost</Text>
                <Text fontSize="0.5rem" fontWeight="light">
                  Available - Accounted
                </Text>
              </Center>
              {details.map((item, index) => {
                let lost = calculateLost(item);
                let used = item.ret + lost;

                return (
                  <Flex
                    key={`${item.sku._id}-${index}`}
                    flex={1}
                    direction="row"
                    bg={index % 2 === 0 ? '#FAEBED' : '#FDF7F8'}>
                    <Text
                      flex={1}
                      p="15px"
                      borderBottom={
                        index % 2 === 0 ? 'surface.200' : 'surface.100'
                      }>
                      {formatNumber(lost)}
                    </Text>
                  </Flex>
                );
              })}
              <Flex
                bg="#F1C1C8"
                borderBottomRadius="10px"
                flex={1}
                zIndex="100"
                direction="row"
                fontWeight="bold"
                maxW="16rem">
                <Text flex={1} p="15px">
                  {formatNumber(values.lost)}
                </Text>
              </Flex>
              {/*<Box
                borderRadius="10px"
                borderY="1px solid #F4A0AF"
                borderRight="1px solid #F4A0AF"
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex={100}
                flexGrow={1}></Box>*/}
            </Box>

            {/*   BOX BLUE    */}
            <Box
              position="relative"
              w="5.9rem"
              borderRadius="10px"
              borderY="1px solid #a3d5db"
              borderRight="1px solid #a3d5db">
              <Flex
                flex={1}
                flexDir="column"
                borderTopRadius="10px"
                bg="#A5CFEE"
                h="98px"
                justifyContent="center"
                fontWeight="semibold">
                <Text>Used</Text>
                <Text fontSize="0.5rem" fontWeight="light">
                  Dirty + Lost
                </Text>
              </Flex>
              {details.map((item, index) => {
                let lost = calculateLost(item);
                let used = item.ret + lost;

                return (
                  <Flex
                    key={`${item.sku._id}-${index}`}
                    flex={1}
                    direction="row"
                    bg={index % 2 === 0 ? '#e0f3f6' : '#eaf8fb'}>
                    <Text
                      flex={1}
                      p="15px"
                      borderBottom={index % 2 === 0 ? '#e0f3f6' : '#eaf8fb'}>
                      {formatNumber(used)}
                    </Text>
                  </Flex>
                );
              })}
              <Flex
                bg="#A5CFEE"
                borderBottomRadius="10px"
                flex={1}
                zIndex="100"
                direction="row"
                fontWeight="bold"
                maxW="16rem">
                <Text flex={1} p="15px">
                  {formatNumber(values.used)}
                </Text>
              </Flex>
              {/*<Box
                borderRadius="10px"
                borderY="1px solid #a3d5db"
                borderRight="1px solid #a3d5db"
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex={100}
                flexGrow={1}></Box>*/}
            </Box>

            {/*   BOX GREEN    */}
            <Box
              position="relative"
              w="5.9rem"
              borderRadius="10px"
              borderY="1px solid #9fcba8"
              borderRight="1px solid #9fcba8">
              <Flex
                flex={1}
                flexDir="column"
                borderTopRadius="10px"
                bg="#AEE6B4"
                h="98px"
                px={2}
                justifyContent="center"
                fontWeight="semibold">
                <Text>Return Rate</Text>
                <Text fontSize="0.5rem" fontWeight="light">
                  Accounted For / Available
                </Text>
              </Flex>

              {details.map((item, index) => {
                let lost = calculateLost(item);
                let used = item.ret + lost;

                return (
                  <Flex
                    key={`${item.sku._id}-${index}`}
                    flex={1}
                    direction="row"
                    bg={index % 2 === 0 ? '#d9f5dc' : '#e6fce8'}>
                    <Text
                      flex={1}
                      p="15px"
                      borderBottom={index % 2 === 0 ? '#d9f5dc' : '#e6fce8'}>
                      {isNaN(returnRateEach(item))
                        ? 0
                        : returnRateEach(item).toFixed(2)}
                      %
                    </Text>
                  </Flex>
                );
              })}
              <Flex
                bg="#AEE6B4"
                borderBottomRadius="10px"
                flex={1}
                zIndex="100"
                direction="row"
                fontWeight="bold"
                maxW="16rem">
                <Text flex={1} p="15px">
                  {returnRate(values).toFixed(2)}%
                </Text>
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </Flex>
      <Flex marginTop="10px">
        <Flex width="23%"></Flex>
        <Flex justify="center" w="12rem">
          {renderCountLink(
            report?.totalOrders,
            (report?.totalOrders || 0) === 1 ? 'Delivery' : 'Deliveries',
            routesSettings.PORTAL_ORDERS.getPath(),
            siteId,
          )}
        </Flex>
        <Flex justify="center" w="15rem">
          {renderCountLink(
            report?.totalPickups,
            (report?.totalPickups || 0) === 1 ? 'Pickup' : 'Pickups',
            routesSettings.PORTAL_PICKUPS.getPath(),
            siteId,
          )}
        </Flex>
      </Flex>
      {/*<Flex marginTop="10px">
        {' '}
        <CopyAsCSv data={csvData} title="Copy With Format" />
      </Flex>*/}
    </Flex>
  );
};
export default SkuDetailTab;
